// Analytics service - tracks and fetches article view statistics
import { supabase } from '../config/supabase.js';
import { formatCategoriesDisplay } from './articleCategories.js';

const inFlightViews = new Set();

const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

const fallbackIncrementViewCount = async (articleId) => {
  const { data: article, error: fetchError } = await supabase
    .from('articles')
    .select('view_count')
    .eq('id', articleId)
    .single();

  if (fetchError) {
    throw fetchError;
  }

  const { error: updateError } = await supabase
    .from('articles')
    .update({
      view_count: (article?.view_count || 0) + 1,
      last_viewed_at: new Date().toISOString(),
    })
    .eq('id', articleId);

  if (updateError) {
    throw updateError;
  }
};

const incrementViewCount = async (articleId) => {
  const { error: rpcError } = await supabase.rpc('increment_article_view_count', {
    p_article_id: articleId,
  });

  if (!rpcError) {
    return;
  }

  // RPC missing or not deployed yet — fall back to read-then-update
  if (rpcError.code === 'PGRST202' || rpcError.code === '42883') {
    await fallbackIncrementViewCount(articleId);
    return;
  }

  throw rpcError;
};

export const trackArticleView = async (articleId) => {
  if (!articleId) return;

  const sessionKey = `article_view_${articleId}`;
  if (sessionStorage.getItem(sessionKey) || inFlightViews.has(articleId)) {
    return;
  }

  inFlightViews.add(articleId);

  try {
    const { error: insertError } = await supabase.from('article_views').insert({
      article_id: articleId,
      viewed_at: new Date().toISOString(),
      user_agent: navigator.userAgent,
      session_id: getSessionId(),
      referrer: document.referrer || null,
    });

    if (insertError) {
      console.error('Error tracking article view:', insertError);
      return;
    }

    await incrementViewCount(articleId);
    sessionStorage.setItem(sessionKey, 'true');
  } catch (error) {
    console.error('Error in trackArticleView:', error);
  } finally {
    inFlightViews.delete(articleId);
  }
};

export const getAnalytics = async () => {
  const { data: viewData, error: viewError } = await supabase
    .from('articles')
    .select('id, title, categories, view_count, last_viewed_at, published_date')
    .eq('is_published', true)
    .order('view_count', { ascending: false });

  if (viewError) {
    throw viewError;
  }

  const articles = viewData || [];
  const totalViews = articles.reduce((sum, article) => sum + (article.view_count || 0), 0);
  const articlesWithViews = articles.filter((article) => (article.view_count || 0) > 0);
  const uniqueArticles = articlesWithViews.length;
  const averageViews =
    uniqueArticles > 0 ? Math.round(totalViews / uniqueArticles) : 0;
  const topArticle = articlesWithViews[0] || null;

  const articleViews = articles.map((article) => ({
    id: article.id,
    title: article.title,
    category: formatCategoriesDisplay(article.categories),
    views: article.view_count || 0,
    lastViewed: article.last_viewed_at,
    publishedDate: article.published_date,
  }));

  return {
    totalViews,
    uniqueArticles,
    publishedArticles: articles.length,
    averageViews,
    topArticle: topArticle
      ? {
          title: topArticle.title,
          views: topArticle.view_count || 0,
        }
      : null,
    articleViews,
  };
};

export const getArticleViewHistory = async (articleId) => {
  const { data, error } = await supabase
    .from('article_views')
    .select('*')
    .eq('article_id', articleId)
    .order('viewed_at', { ascending: false })
    .limit(100);

  if (error) {
    throw error;
  }

  return data || [];
};

export const getViewsByDateRange = async (startDate, endDate) => {
  const { data, error } = await supabase
    .from('article_views')
    .select('viewed_at, article_id')
    .gte('viewed_at', startDate)
    .lte('viewed_at', endDate)
    .order('viewed_at', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
};
